import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * The hub only builds floating launchers. `manual` (a button on the site opens
 * the widget) is legacy: the loader still honours it, but templates saved here
 * are always floating.
 */
export type WidgetLauncherMode = 'floating' | 'manual'
export type WidgetPosition = 'bottom-right' | 'bottom-left'

/**
 * `single` — every site opens its own venue's WhatsApp directly.
 * `multi` — every site shows a picker with all venues assigned to the template.
 */
export type WidgetTemplateKind = 'single' | 'multi'

/** Derived server-side from the assistant's WhatsApp sender; never stored. */
export type WidgetConnectionState = 'live' | 'verifying' | 'action_needed' | 'not_connected'

export type WidgetButtonShape = 'circle' | 'square'
export type WidgetButtonSize = 'S' | 'M' | 'L'

export interface WidgetColors {
  action_buttons: string
  panel_background: string
  whatsapp_button: string | null
  /** Glyph colour inside the launcher. Absent on templates saved before the redesign. */
  whatsapp_icon?: string
}

/**
 * Styling only. Destinations and widget keys belong to the assistant.
 *
 * The hub API stores `shape`, `size`, `icon_only`, and `whatsapp_icon`. They stay
 * optional so a template saved before those fields existed still loads, with
 * defaults applied in `widgetConfig.serializers`.
 */
export interface WidgetTemplateStyle {
  launcher_mode: WidgetLauncherMode
  position: WidgetPosition
  offset_x: number
  offset_y: number
  colors: WidgetColors
  shape?: WidgetButtonShape
  size?: WidgetButtonSize
  /** Opens WhatsApp straight from the launcher, with no message card. */
  icon_only?: boolean
}

/** An assistant this template is assigned to, returned alongside the template. */
export interface WidgetTemplateAssignment {
  assistant_id: number
  assistant_name: string
  widget_key: string
  embed_snippet: string
  connection_state: WidgetConnectionState
  website_live: boolean
  website_last_seen_at: string | null
  /** Shown under the name in a multi-venue location picker. */
  address: string | null
}

export interface WidgetTemplate {
  id: number
  customer_id: number
  name: string
  kind: WidgetTemplateKind
  style: WidgetTemplateStyle
  assignments: WidgetTemplateAssignment[]
  created_at?: string
  updated_at?: string
}

export interface WidgetTemplateUpsert {
  name: string
  kind: WidgetTemplateKind
  style: WidgetTemplateStyle
}

export interface WidgetTemplateCreate extends WidgetTemplateUpsert {
  customer_id?: number
}

/** One row of the hub's assistants tab, scoped to the current user's accessible customers. */
export interface WidgetOverviewRow {
  assistant_id: number
  customer_id: number
  customer_name: string
  assistant_name: string
  phone_number: string | null
  connection_state: WidgetConnectionState
  template_id: number | null
  template_name: string | null
  template_color: string | null
  widget_key: string | null
  embed_snippet: string | null
  website_live: boolean
  website_last_seen_at: string | null
  /** Of the assigned template — a multi template needs a venue on the trigger. */
  template_kind: WidgetTemplateKind | null
  /** Meta pre-verification of the assistant's number (pending = session in flight). */
  preverify_status: 'idle' | 'armed' | 'listening' | 'verified' | 'failed' | 'technical'
}

export const widgetKeys = {
  all: ['widgets'] as const,
  overview: () => [...widgetKeys.all, 'overview'] as const,
  templates: () => [...widgetKeys.all, 'templates'] as const,
  template: (id: number) => [...widgetKeys.all, 'templates', id] as const,
}

/** Every accessible assistant with its connection state and assignment. */
export function useWidgetOverview() {
  return useQuery({
    queryKey: widgetKeys.overview(),
    queryFn: async (): Promise<WidgetOverviewRow[]> => {
      const response = await api.get('/widgets/overview')
      return response.data
    },
    staleTime: 30 * 1000,
  })
}

export function useWidgetTemplates() {
  return useQuery({
    queryKey: widgetKeys.templates(),
    queryFn: async (): Promise<WidgetTemplate[]> => {
      const response = await api.get('/widgets/templates')
      return response.data
    },
    staleTime: 30 * 1000,
  })
}

export function useWidgetTemplate(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: widgetKeys.template(id),
    queryFn: async (): Promise<WidgetTemplate> => {
      const response = await api.get(`/widgets/templates/${id}`)
      return response.data
    },
    enabled: options?.enabled ?? !!id,
    staleTime: 30 * 1000,
  })
}

/** Kick off a hub refetch without making the click wait for it. */
function refreshWidgetHub(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: widgetKeys.all })
}

export function useCreateWidgetTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: WidgetTemplateCreate): Promise<WidgetTemplate> => {
      const response = await api.post('/widgets/templates', payload)
      return response.data
    },
    onSuccess: () => refreshWidgetHub(queryClient),
  })
}

export function useUpdateWidgetTemplate(id: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: WidgetTemplateUpsert): Promise<WidgetTemplate> => {
      const response = await api.put(`/widgets/templates/${id}`, payload)
      return response.data
    },
    onSuccess: () => refreshWidgetHub(queryClient),
  })
}

export function useDeleteWidgetTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await api.delete(`/widgets/templates/${id}`)
    },
    onSuccess: () => refreshWidgetHub(queryClient),
  })
}

/**
 * Assign a template to an assistant, or move it to a different one. The
 * assistant keeps its widget key, so installed embeds keep resolving.
 */
export function useAssignWidgetTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: { assistant_id: number; template_id: number }) => {
      const response = await api.put(`/widgets/assignments/${variables.assistant_id}`, {
        template_id: variables.template_id,
      })
      return response.data
    },
    onSuccess: () => refreshWidgetHub(queryClient),
  })
}

/**
 * Set the address a venue shows in the location picker. Like assignments, this
 * is a property of the assistant, so it saves without saving the template.
 */
export function useUpdateWidgetVenueAddress() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: { assistant_id: number; address: string | null }): Promise<void> => {
      await api.put(`/widgets/assignments/${variables.assistant_id}/address`, {
        address: variables.address,
      })
    },
    onSuccess: () => refreshWidgetHub(queryClient),
  })
}

export function useUnassignWidgetTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (assistantId: number): Promise<void> => {
      await api.delete(`/widgets/assignments/${assistantId}`)
    },
    onSuccess: () => refreshWidgetHub(queryClient),
  })
}
